import { Box, BoxProps, forwardRef } from "@chakra-ui/react";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import * as React from "react";

const DEFAULT_SCROLLBAR_SIZE = "8px";

const ScrollArea = forwardRef<
	React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> &
		Omit<BoxProps, "position" | "overflow" | "as"> & {
			scrollbarSize?: string;
		},
	"div"
>(
	(
		{
			className,
			children,
			scrollbarSize = DEFAULT_SCROLLBAR_SIZE,
			...props
		},
		ref
	) => (
		<Box
			as={ScrollAreaPrimitive.Root}
			ref={ref}
			position="relative"
			overflow="hidden"
			{...props}
		>
			<Box
				as={ScrollAreaPrimitive.Viewport}
				h="full"
				w="full"
				borderRadius="inherit"
			>
				{children}
			</Box>
			<ScrollBar scrollbarSize={scrollbarSize} />
			<ScrollAreaPrimitive.Corner />
		</Box>
	)
);

ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

const ScrollBar = forwardRef<
	React.ComponentPropsWithoutRef<
		typeof ScrollAreaPrimitive.ScrollAreaScrollbar
	> & {
		scrollbarSize?: string;
	},
	"div"
>(
	(
		{
			className,
			orientation = "vertical",
			scrollbarSize = DEFAULT_SCROLLBAR_SIZE,
			...props
		},
		ref
	) => (
		<Box
			as={ScrollAreaPrimitive.ScrollAreaScrollbar}
			ref={ref}
			orientation={orientation}
			display="flex"
			userSelect="none"
			h={orientation === "vertical" ? "full" : scrollbarSize}
			w={orientation === "vertical" ? scrollbarSize : undefined}
			p="1px"
			borderLeftWidth={orientation === "vertical" ? "1px" : undefined}
			borderLeftColor={
				orientation === "vertical" ? "transparent" : undefined
			}
			borderTopWidth={orientation === "horizontal" ? "1px" : undefined}
			borderTopColor={
				orientation === "horizontal" ? "transparent" : undefined
			}
			flexDirection={orientation === "vertical" ? "column" : undefined}
			sx={{
				touchAction: "none",
			}}
			transitionProperty="color, background-color, border-color, text-decoration-color, fill, stroke"
			transitionTimingFunction="cubic-bezier(0.4, 0, 0.2, 1)"
			transitionDuration="150ms"
			{...props}
		>
			<Box
				as={ScrollAreaPrimitive.ScrollAreaThumb}
				position="relative"
				flex={1}
				borderRadius="full"
				bgColor="gray.200"
			/>
		</Box>
	)
);
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

export { ScrollArea, ScrollBar };
